import { BasicButton, Loading, Typography } from "@/components";
import env from "@/env.config";
import useProfile from "@/hooks/useProfile";
import useUpdateMember from "@/hooks/useUpdateMember";
import { cn } from "@/libraries/cn";
import { getStatusColor } from "@/libraries/colors";
import { USER_STATUS } from "@/libraries/constant";
import { capitalized, isAxiosError, randUuid } from "@/libraries/helper";
import { authService } from "@/services";
import { useDownloadStore } from "@/stores/download";
import { useHemiStore } from "@/stores/hemi";
import { usePrompt } from "@/stores/prompt";
import { useSyncStore } from "@/stores/sync";
import BalanceIcon from "@mui/icons-material/Balance";
import CircleIcon from "@mui/icons-material/Circle";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import KeyboardBackspace from "@mui/icons-material/KeyboardBackspace";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonIcon from "@mui/icons-material/Person";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import ShareIcon from "@mui/icons-material/Share";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { capitalize } from "lodash";
import { Fragment, useCallback, useState } from "react";
import OneSignal from "react-onesignal";
import { toast } from "sonner";
import AccountInfoStatus from "./-components/AccountInfoStatus";

export const Route = createFileRoute("/dashboard/accounts/")({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();
	const router = useRouter();
	const { profile: userData, isLoading } = useProfile();
	const [showStatus, setShowStatus] = useState(false);
	const [showAvatarPopup, setShowAvatarPopup] = useState(false);
	const [userStatus, setUserStatus] = useState(userData?.status ?? "available");

	const onSuccessSignout = useCallback(async () => {
		await queryClient.cancelQueries();
		Sentry.isEnabled() && Sentry.setUser(null);
		useSyncStore.persist.clearStorage();
		useHemiStore.persist.clearStorage();
		useDownloadStore.persist.clearStorage();
		env.isSecureHttp && OneSignal.logout();
		queryClient.clear();
		router.history.flush();
	}, [queryClient, router]);

	const { mutateAsync: mutateAsyncLogout } = useMutation({
		mutationFn: () => authService.logout(),
		onSuccess: () => {
			onSuccessSignout();
			return router.invalidate();
		},
		onError: (err) => {
			if (isAxiosError(err, 404)) {
				onSuccessSignout();
				return navigate({
					to: "/",
					replace: true,
				});
			}
			return toast.error("Failed to logout");
		},
	});

	const updateUser = useUpdateMember();

	const handleLogout = useCallback(() => {
		return mutateAsyncLogout();
	}, [mutateAsyncLogout]);

	const handleOnOpenDrawerStatus = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			e.preventDefault();
			setShowStatus(true);
		},
		[],
	);

	const handleOnCloseDrawerStatus = useCallback(
		(e?: React.KeyboardEvent | React.MouseEvent) => {
			e?.preventDefault();
			setShowStatus(false);
		},
		[],
	);

	const handleOnSetStatus = useCallback(() => {
		if (!userData?.id) return;
		return updateUser.mutate(
			{
				id: userData?.id,
				status: userStatus,
			},
			{
				onSuccess: () => {
					handleOnCloseDrawerStatus();
				},
			},
		);
	}, [updateUser, userData, handleOnCloseDrawerStatus, userStatus]);

	const openPrompt = usePrompt((state) => state.open);

	const onClickNotification = async (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		const isPermissionGranted = OneSignal.Notifications.permission;
		return openPrompt({
			title: "Notification Settings",
			description: isPermissionGranted
				? "You have already enabled notifications."
				: "To stay updated, please allow browser notifications. You can enable it in your browser settings, by click the reset browser permission.",
			negativeButton: {
				label: "Close",
				variant: "contained",
			},
		});
	};

	if (isLoading) {
		return <Loading status={isLoading} />;
	}

	return (
		<>
			<div className="h-full bg-gray-light">
				<div className="h-full mx-auto">
					<div className="relative wrapper bg-gradient-account mx-auto">
						<Link to="/dashboard" className="flex justify-start items-center">
							<IconButton aria-label="Back">
								<KeyboardBackspace className="text-white" />
							</IconButton>
							<Typography fontWeight={600} fontSize={14} color="white">
								Settings
							</Typography>
						</Link>
						<Box className="flex flex-col items-center py-16">
							{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
							<div
								className="w-24 h-24 bg-white border border-solid border-white rounded-full overflow-hidden p-1 my-2"
								onClick={() => setShowAvatarPopup(true)}
							>
								<img
									className="w-full h-full object-cover rounded-full"
									src={userData?.avatar ?? "/images/blank-avatar.svg"}
									alt={userData?.fullname ?? "L"}
									loading="eager"
								/>
							</div>
							<p className="text-xl text-white font-bold">
								{userData?.fullname ?? "-"}
							</p>
							<Chip
								icon={
									<CircleIcon
										fontSize="small"
										style={{ fill: getStatusColor(userData?.status as any) }}
									/>
								}
								label={capitalized(userData?.status as any)}
								className="!bg-white"
							/>
						</Box>
					</div>
					<Box
						pt={!userData?.case_left ? 25 : !userData._isProPlan ? 10 : 5}
						className="container relative flex flex-col gap-3 wrapper bg-gray-light mx-auto"
					>
						<Box className="absolute inset-x-0 -top-12 px-3">
							<Box
								className={cn(
									"w-full flex flex-col gap-2 bg-gradient-subscription rounded-xl",
									{
										"pt-2.5": !userData?.case_left,
										"py-2.5": !!userData?.case_left,
									},
								)}
							>
								<Link to={"/manage-subscription"}>
									<Box className="px-4 flex justify-between items-center">
										<Box className="flex items-center gap-4">
											<img
												className="w-12 h-12"
												src="/images/icon-health-and-safety.svg"
												alt="health-and-safety"
												loading="eager"
											/>
											<Box>
												<p className="text-dark text-sm font-medium">
													My Subscription
												</p>
												<div className="w-fit flex flex-row justify-center items-center gap-1">
													<p className="text-xs font-medium">
														{userData?._isFreePlan
															? "Free Plan"
															: `${capitalize(userData?.subscription_status)} Plan`}
													</p>
													{!userData?._isFreePlan &&
													userData?.subscription_plan?.status ? (
														<Chip
															label={capitalize(
																userData.subscription_plan.status,
															)}
															size="small"
															variant="filled"
															color={
																userData.subscription_plan.status.toLowerCase() ===
																"active"
																	? "primary"
																	: "error"
															}
															className="!rounded-lg"
														/>
													) : null}
												</div>
											</Box>
										</Box>
										<KeyboardArrowRightOutlinedIcon className="text-xl" />
									</Box>
								</Link>
								{!userData?._isProPlan ? (
									<AccountInfoStatus
										planStartOn={userData?.subscription_plan?.start_on}
										isFreePlan={userData?._isFreePlan || false}
										isPlusPlan={userData?._isPlusPlan || false}
										isProPlan={userData?._isProPlan || false}
										caseLeft={userData?.case_left || 0}
										maxCase={userData?._maxCase || 0}
									/>
								) : null}
							</Box>
						</Box>
						<Box className="bg-white rounded-xl text-dark px-3">
							<Link
								to="/dashboard/accounts/settings/account"
								className="flex justify-start items-center gap-1 border-b border-light-gray py-3"
							>
								<ManageAccountsIcon />
								<p className="text-sm font-medium flex flex-1">
									Account Settings
								</p>
								<KeyboardArrowRightOutlinedIcon />
							</Link>
							<Link to="/dashboard/accounts/settings/profile">
								<Box
									component="span"
									className="flex justify-start items-center gap-1 border-b border-light-gray py-3"
								>
									<PersonIcon />
									<p className="text-sm font-medium flex flex-1">
										Profile Settings
									</p>
									<KeyboardArrowRightOutlinedIcon />
								</Box>
							</Link>
						</Box>
						<Box className="bg-white rounded-xl text-dark px-3">
							<Box
								className="flex justify-start items-center gap-1 border-b border-light-gray py-3 cursor-pointer"
								onClick={handleOnOpenDrawerStatus}
							>
								<RadioButtonCheckedIcon />
								<p className="text-sm font-medium flex flex-1">Status</p>
								<KeyboardArrowRightOutlinedIcon />
							</Box>
							<Box
								className="flex justify-start items-center gap-1 border-b border-light-gray py-3 cursor-pointer"
								onClick={onClickNotification}
							>
								<NotificationsIcon />
								<p className="text-sm font-medium flex flex-1">Notifications</p>
								<KeyboardArrowRightOutlinedIcon />
							</Box>
						</Box>
						<Link to="/dashboard/contacts/no-access">
							<Box className="bg-white rounded-xl text-dark px-3">
								<Box className="flex justify-start items-center gap-1 border-b border-light-gray py-3">
									<ShareIcon />
									<p className="text-sm font-medium flex flex-1">Invite</p>
									<KeyboardArrowRightOutlinedIcon />
								</Box>
							</Box>
						</Link>
						<Box className="bg-white rounded-xl text-dark px-3">
							<Box className="flex justify-start items-center gap-1 border-b border-light-gray py-3">
								<BalanceIcon />
								<p className="text-sm font-medium">Legal</p>
							</Box>
							<Link to={"/term-service"}>
								<Box className="flex justify-between items-center py-3">
									<p className="text-sm font-medium">Terms of Use</p>
									<KeyboardArrowRightOutlinedIcon />
								</Box>
							</Link>
							<Link to={"/privacy-policy"}>
								<Box className="flex justify-between items-center py-3">
									<p className="text-sm font-medium">Privacy Policy</p>
									<KeyboardArrowRightOutlinedIcon />
								</Box>
							</Link>
							<Link to={"/patient-policy"}>
								<Box className="flex justify-between items-center py-3">
									<p className="text-sm font-medium">
										Patient Data Protection Policy
									</p>
									<KeyboardArrowRightOutlinedIcon />
								</Box>
							</Link>
							<Link to={"/cookie-policy"}>
								<Box className="flex justify-between items-center py-3">
									<p className="text-sm font-medium">Cookie Policy</p>
									<KeyboardArrowRightOutlinedIcon />
								</Box>
							</Link>
						</Box>
						<BasicButton
							variant="contained"
							color="warning"
							onClick={handleLogout}
							className="w-full !h-14 !text-base !border !border-solid !border-light-gray !shadow-none !my-4"
						>
							<Box className="w-full flex justify-center items-center gap-1">
								<LogoutIcon />
								<p>Sign Out</p>
							</Box>
						</BasicButton>
					</Box>
				</div>
			</div>
			{/* avatar pop up */}
			<Modal
				open={showAvatarPopup}
				onClose={() => setShowAvatarPopup(false)}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box className="absolute wrap top-2/4 left-1/2 -translate-y-1/2 -translate-x-1/2 !border-none !outline-none">
					<Box className="w-64">
						<img
							className="w-full"
							src={userData?.avatar ?? "/images/blank-avatar.svg"}
							alt={userData?.fullname ?? "L"}
						/>
					</Box>
				</Box>
			</Modal>
			<Drawer
				anchor="bottom"
				open={showStatus}
				onClose={(e: any) => handleOnCloseDrawerStatus(e)}
				classes={{
					paper: "bg-white rounded-tr-2xl rounded-tl-2xl",
				}}
			>
				<Box className="wrapper flex flex-col gap-3 mx-auto p-4">
					<FormControl fullWidth>
						<FormLabel
							id="user-status-choices"
							className="!font-bold !text-xl !text-dark"
						>
							Status
						</FormLabel>
						<RadioGroup
							aria-labelledby="user-status-choices"
							defaultValue={userData?.status}
							name="radio-buttons-group"
							onChange={(e) => setUserStatus(e.target.value as any)}
						>
							{Object.entries(USER_STATUS).map(([, value]) => (
								<Fragment key={randUuid()}>
									<FormControlLabel
										value={value}
										labelPlacement="start"
										control={<Radio />}
										label={
											<>
												<CircleIcon
													fontSize="small"
													className="mr-2"
													style={{ fill: getStatusColor(value) }}
												/>
												<span>{capitalized(value)}</span>
											</>
										}
										classes={{
											root: "justify-between !m-0",
										}}
									/>
									<Divider />
								</Fragment>
							))}
						</RadioGroup>
					</FormControl>
					<Box className="flex items-center justify-center gap-4">
						<BasicButton
							variant="outlined"
							color="inherit"
							className="w-full"
							onClick={handleOnCloseDrawerStatus}
						>
							<span className="text-primary">Cancel</span>
						</BasicButton>
						<BasicButton
							variant="outlined"
							color="primary"
							className="!bg-primary w-full"
							onClick={handleOnSetStatus}
						>
							<span className="text-white">Set</span>
						</BasicButton>
					</Box>
				</Box>
			</Drawer>
			<Loading status={updateUser.isPending} />
		</>
	);
}
